import { motion, AnimatePresence } from "motion/react";

interface ReportSamplePreviewProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ReportSamplePreview({ isOpen, onClose }: ReportSamplePreviewProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          ></motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="relative bg-white rounded-2xl max-w-[900px] w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-[#E8E5E0] px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center">
                  <i className="ri-file-search-line text-[#C4956A]"></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1C1917]">물건분석 리포트 샘플</h3>
                  <p className="text-xs text-[#8C8780]">실제 전문가 작성 리포트의 일부입니다</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#8C8780] hover:bg-[#FAFAF8] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Report Content */}
            <div className="p-6 md:p-8 space-y-6 font-['Noto_Sans_KR']">

              {/* Report Header */}
              <div className="border-b-2 border-[#1C1917] pb-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#C4956A]"></div>
                    <span className="text-xs font-semibold text-[#C4956A] tracking-wider uppercase">KANG STUDY</span>
                  </div>
                  <span className="text-xs text-[#8C8780]">Report No. KS-2026-0782</span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-[#1C1917] leading-tight mb-1">
                  경매 물건 권리분석 리포트
                </h2>
                <p className="text-sm text-[#5C5650]">
                  인천 미추홀구 용현동 소재 아파트 | 사건번호 2025타경52341
                </p>
              </div>

              {/* Property Overview Table */}
              <div>
                <h4 className="flex items-center gap-2 text-sm font-bold text-[#1C1917] mb-3">
                  <span className="w-5 h-5 rounded bg-[#1C1917] flex items-center justify-center">
                    <span className="text-[10px] text-white font-bold">1</span>
                  </span>
                  물건 개요
                </h4>
                <div className="border border-[#E8E5E0] rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      {[
                        ["사건번호", "2025타경52341"],
                        ["법원", "인천지방법원"],
                        ["소재지", "인천광역시 미추홀구 용현동 123-45"],
                        ["물건종류", "아파트 (전용면적 84.97㎡)"],
                        ["감정가", "320,000,000원"],
                        ["최저가 (1회 유찰)", "224,000,000원"],
                        ["입찰기일", "2026년 6월 15일"],
                        ["유찰횟수", "1회"],
                      ].map(([label, value], i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-[#FAFAF8]" : "bg-white"}>
                          <td className="px-4 py-2.5 text-[#8C8780] font-medium w-32 border-r border-[#F0EDE8]">{label}</td>
                          <td className="px-4 py-2.5 text-[#1C1917] font-semibold">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Rights Analysis */}
              <div>
                <h4 className="flex items-center gap-2 text-sm font-bold text-[#1C1917] mb-3">
                  <span className="w-5 h-5 rounded bg-[#1C1917] flex items-center justify-center">
                    <span className="text-[10px] text-white font-bold">2</span>
                  </span>
                  권리분석
                </h4>
                <div className="border border-[#E8E5E0] rounded-lg p-5 bg-[#FAFAF8]">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-emerald-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1C1917] mb-1">말소기준권리 분석 결과</p>
                      <p className="text-xs text-[#5C5650] leading-relaxed">
                        본건의 말소기준권리(EBR)는 <strong className="text-[#1C1917]">2021.05.10. 접수된 근저당권(채권최고액 1억 2천만원)</strong>입니다.
                        EBR 이후 설정된 모든 권리는 매각으로 소멸되며, 매수인이 인수해야 할 권리는 없습니다.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-emerald-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1C1917] mb-1">임차인 분석</p>
                      <p className="text-xs text-[#5C5650] leading-relaxed">
                        본건에는 임차인이 존재하지 않아 <strong className="text-[#1C1917]">인도명령으로 신속한 명도가 가능</strong>합니다.
                        별도의 명도소송이나 인수금은 발생하지 않을 것으로 예상됩니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Market Price Comparison */}
              <div>
                <h4 className="flex items-center gap-2 text-sm font-bold text-[#1C1917] mb-3">
                  <span className="w-5 h-5 rounded bg-[#1C1917] flex items-center justify-center">
                    <span className="text-[10px] text-white font-bold">3</span>
                  </span>
                  인근 시세 비교
                </h4>
                <div className="border border-[#E8E5E0] rounded-lg p-5">
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: "인근 실거래가 (하위)", price: "3억 5,000만원", color: "bg-amber-100 text-amber-700" },
                      { label: "인근 실거래가 (평균)", price: "3억 8,000만원", color: "bg-emerald-100 text-emerald-700" },
                      { label: "예상 낙찰가", price: "2억 5,000만원", color: "bg-[#C4956A]/10 text-[#C4956A]" },
                    ].map((item, i) => (
                      <div key={i} className="text-center">
                        <p className="text-xs text-[#8C8780] mb-2">{item.label}</p>
                        <p className={`text-sm font-bold px-3 py-2 rounded-lg inline-block ${item.color}`}>
                          {item.price}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Profit Calculation */}
              <div>
                <h4 className="flex items-center gap-2 text-sm font-bold text-[#1C1917] mb-3">
                  <span className="w-5 h-5 rounded bg-[#1C1917] flex items-center justify-center">
                    <span className="text-[10px] text-white font-bold">4</span>
                  </span>
                  예상 수익 분석
                </h4>
                <div className="border border-[#E8E5E0] rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      {[
                        ["예상 낙찰가 (A)", "250,000,000원", "text-[#1C1917]"],
                        ["취득세 및 등기비용 (B)", "7,500,000원", "text-[#8C8780]"],
                        ["명도 비용 (C)", "0원 (임차인 없음)", "text-emerald-600"],
                        ["예상 총 투입금 (A+B+C)", "257,500,000원", "text-[#1C1917] font-bold"],
                        ["예상 매도가 (D)", "380,000,000원", "text-[#1C1917]"],
                        ["예상 순수익 (D - A+B+C)", "122,500,000원", "text-emerald-600 text-base font-bold"],
                      ].map(([label, value, className], i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-[#FAFAF8]" : "bg-white"}>
                          <td className="px-4 py-2.5 text-[#8C8780] font-medium border-r border-[#F0EDE8]">{label}</td>
                          <td className={`px-4 py-2.5 ${className}`}>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <i className="ri-funds-line text-emerald-600"></i>
                  <span className="text-sm text-emerald-700">
                    <strong>예상 수익률: 약 47.6%</strong> — 투자 적합 판정 (A등급)
                  </span>
                </div>
              </div>

              {/* Risk Assessment */}
              <div>
                <h4 className="flex items-center gap-2 text-sm font-bold text-[#1C1917] mb-3">
                  <span className="w-5 h-5 rounded bg-[#1C1917] flex items-center justify-center">
                    <span className="text-[10px] text-white font-bold">5</span>
                  </span>
                  위험 요소 점검
                </h4>
                <div className="border border-[#E8E5E0] rounded-lg divide-y divide-[#F0EDE8]">
                  {[
                    { label: "대위변제 위험", status: "없음", icon: "ri-shield-check-line", color: "text-emerald-500", bg: "bg-emerald-50" },
                    { label: "유치권 신고", status: "없음", icon: "ri-shield-check-line", color: "text-emerald-500", bg: "bg-emerald-50" },
                    { label: "위반건축물", status: "해당 없음", icon: "ri-shield-check-line", color: "text-emerald-500", bg: "bg-emerald-50" },
                    { label: "명도 난이도", status: "낮음 (인도명령)", icon: "ri-information-line", color: "text-amber-500", bg: "bg-amber-50" },
                  ].map((risk, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm text-[#5C5650]">{risk.label}</span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${risk.bg} ${risk.color}`}>
                        <i className={risk.icon}></i>
                        {risk.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer Note */}
              <div className="border-t border-[#E8E5E0] pt-4 text-center">
                <p className="text-xs text-[#8C8780]">
                  본 리포트는 샘플이며, 실제 리포트에는 더욱 상세한 분석과 함께 변호사 검토 의견, 현장 실사 사진 등이 포함됩니다.
                </p>
              </div>

            </div>

            {/* Bottom CTA */}
            <div className="sticky bottom-0 bg-white border-t border-[#E8E5E0] px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-b-2xl">
              <div className="flex items-center gap-3">
                <i className="ri-information-line text-[#C4956A] text-lg"></i>
                <p className="text-sm text-[#5C5650] leading-relaxed">
                  실제 리포트에는 <strong className="text-[#1C1917]">전체 권리분석, 시세비교, 예상수익률</strong>까지 상세히 담겨 있습니다.
                </p>
              </div>
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-[#C4956A] text-white text-sm font-semibold rounded-full hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-shopping-cart-line"></i>
                리포트 구매하러 가기
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}